// filterView/conditionSet.js — the condition list of the documents.php filter views (plan documents_admin-search,
// phase filter-client). Pure functions: each returns a new list and leaves the old one as it was (store.set(s => …)).
//
//   set = { v: 1, combine: 'and' | 'or', conditions: [{ id, field, mode, value }] }
//
// `id` lives in the browser only (stable key for the views: 'c1', 'c2' …) and is not sent. `value` is a string, or
// absent for a mode without value. The schema (DocumentFilterSchema on the server) says which fields, modes and
// choices exist; the server checks the sent list again (DocumentFilterQuery::conditionSet) and stays the authority.
//
// Decisions (User 2026-10-13): a condition without a value is left out of fv, its row stays in the view; a rejected
// list on page load keeps its valid conditions, fromParam reports the rest as problems.

const COMBINES = ['and', 'or'];
const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
// one document id between commas, as the server reads it (DocumentFilterQuery::ID_TOKEN): optional braces and prefix
// "ABC:", leading zeros, optionally "-" and a second number = a range
const ID_TOKEN = /^\{?\s*(?:[^\s:,{}]{3,4}:)?0*(\d+)\s*(?:-\s*(?:[^\s:,{}]{3,4}:)?0*(\d+))?\s*\}?$/u;

const fieldOf = (schema, key) => schema.fields.find((f) => f.key === key);
const modeOf = (field, key) => field.modes.find((m) => m.key === key);
const isBlank = (value) => typeof value !== 'string' || value.trim() === '';

function nextId(conditions) {
    const numbers = conditions.map((c) => Number(String(c.id).slice(1))).filter(Number.isFinite);
    return `c${numbers.length ? Math.max(...numbers) + 1 : 1}`;
}

function requireField(schema, key) {
    const field = fieldOf(schema, key);
    if (!field) {
        throw new Error(`[conditionSet] unknown field ${key}`);
    }
    return field;
}

function requireIndex(set, id) {
    const index = set.conditions.findIndex((c) => c.id === id);
    if (index < 0) {
        throw new Error(`[conditionSet] no condition ${id}`);
    }
    return index;
}

// The value a new condition of this field starts with: a list field its first choice (a select always shows one),
// others an empty text.
function startValue(field) {
    return field.kind === 'list' && field.options?.length ? field.options[0].value : '';
}

// A condition with mode and value fitted to the field: an unknown mode becomes the field's first, a mode without
// value drops the value.
function fit(field, condition) {
    const mode = modeOf(field, condition.mode) ? condition.mode : field.modes[0].key;
    const { value, ...rest } = { ...condition, field: field.key, mode };
    return modeOf(field, mode).value ? { ...rest, value: typeof value === 'string' ? value : startValue(field) } : rest;
}

// A new list from the schema's start (defaults) or from the given { combine, conditions } (without ids).
export function createSet(schema, initial = schema.defaults) {
    let set = { v: schema.v, combine: initial.combine ?? 'and', conditions: [] };
    for (const condition of initial.conditions ?? []) {
        set = addCondition(set, schema, condition);
    }
    return set;
}

// Adds { field, mode?, value? } at the end; mode defaults to the field's first mode, value to its start value.
export function addCondition(set, schema, { field, mode, value }) {
    const condition = fit(requireField(schema, field), { id: nextId(set.conditions), mode, value });
    return { ...set, conditions: [...set.conditions, condition] };
}

// Changes field / mode / value of one condition. Another field keeps the text when both are text or date fields of the
// same kind (switching "Titel" to "Beschreibung" keeps "Hochzeit"), otherwise it starts again.
export function updateCondition(set, schema, id, patch) {
    const index = requireIndex(set, id);
    const old = set.conditions[index];
    let next = { ...old, ...patch };
    if (patch.field !== undefined && patch.field !== old.field) {
        const from = requireField(schema, old.field);
        const to = requireField(schema, patch.field);
        if (patch.value === undefined && (from.kind !== to.kind || to.kind === 'list')) {
            delete next.value;
        }
    }
    next = fit(requireField(schema, next.field), next);
    const conditions = set.conditions.slice();
    conditions[index] = next;
    return { ...set, conditions };
}

export function removeCondition(set, id) {
    const index = requireIndex(set, id);
    return { ...set, conditions: set.conditions.filter((_, i) => i !== index) };
}

export function setCombine(set, combine) {
    if (!COMBINES.includes(combine)) {
        throw new Error(`[conditionSet] combine must be and / or, got ${combine}`);
    }
    return { ...set, combine };
}

// Document ids: every token fits; ranges only for "is", one id for "after" / "before".
function idsFit(value, mode) {
    const tokens = value.split(',').map((t) => t.trim()).filter((t) => t !== '');
    const ids = tokens.map((t) => ID_TOKEN.exec(t));
    if (!ids.length || ids.some((m) => m === null)) {
        return false;
    }
    const hasRange = ids.some((m) => m[2] !== undefined && Number(m[2]) !== Number(m[1]));
    if (mode !== 'is' && hasRange) {
        return false;
    }
    return !((mode === 'after' || mode === 'before') && ids.length > 1);
}

// Why one condition does not fit the schema, or null. 'value' = a mode with value but none given.
function problemOf(schema, condition) {
    const field = fieldOf(schema, condition?.field);
    if (!field) {
        return 'field';
    }
    const mode = modeOf(field, condition.mode);
    if (!mode) {
        return 'mode';
    }
    if (!mode.value) {
        return null;
    }
    if (isBlank(condition.value)) {
        return 'value';
    }
    if (field.options && !field.options.some((o) => o.value === condition.value)) {
        return 'option';
    }
    if (field.kind === 'id' && !idsFit(condition.value, condition.mode)) {
        return 'id';
    }
    if (field.kind === 'date') {
        const date = DATE.exec(condition.value);
        const parsed = date && new Date(Date.UTC(+date[1], +date[2] - 1, +date[3]));
        if (!parsed || parsed.getUTCMonth() !== +date[2] - 1 || parsed.getUTCDate() !== +date[3]) {
            return 'date';
        }
    }
    return null;
}

// [{ id, reason }] for every condition that does not fit: field, mode, value, option, date, id.
export function validate(set, schema) {
    return set.conditions
        .map((condition) => ({ id: condition.id, reason: problemOf(schema, condition) }))
        .filter((p) => p.reason !== null);
}

// The list as JSON for the hidden fv: without ids, conditions without a value left out (as tab 1 ignores empty
// fields). Other problems stay in — the server rejects them visibly.
export function toParam(set, schema) {
    const conditions = set.conditions
        .filter((c) => problemOf(schema, c) !== 'value')
        .map(({ field, mode, value }) => (value === undefined ? { field, mode } : { field, mode, value }));
    return JSON.stringify({ v: set.v, combine: set.combine, conditions });
}

// The list from a submitted fv. Empty = first visit → the schema's start. Otherwise the valid conditions are kept,
// the others come back as problems [{ index, reason, condition }] (reason 'json' / 'version' for the whole list).
export function fromParam(json, schema) {
    if (json === '' || json === undefined || json === null) {
        return { set: createSet(schema), problems: [] };
    }
    let parsed;
    try {
        parsed = JSON.parse(json);
    } catch (error) {
        return { set: createSet(schema), problems: [{ index: null, reason: 'json', condition: null }] };
    }
    if (!parsed || typeof parsed !== 'object' || parsed.v !== schema.v || !Array.isArray(parsed.conditions)) {
        return { set: createSet(schema), problems: [{ index: null, reason: 'version', condition: null }] };
    }

    const problems = [];
    let set = { v: schema.v, combine: COMBINES.includes(parsed.combine) ? parsed.combine : 'and', conditions: [] };
    if (set.combine !== parsed.combine) {
        problems.push({ index: null, reason: 'combine', condition: null });
    }
    parsed.conditions.forEach((condition, index) => {
        const reason = problemOf(schema, condition);
        if (reason) {
            problems.push({ index, reason, condition });
        } else {
            set = addCondition(set, schema, condition);
        }
    });
    return { set, problems };
}
